import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { House } from '@/client';
import useHouseTasks from '@/hooks/house/documents/useHouseTasks.ts';
import { isTaskCompleted, isTaskFailed, isTaskInProgress, isTaskQueued } from '@/utils/taskUtils.ts';
import { isQuotaExhausted, isRateLimitError } from '@/utils/httpErrors.ts';
import { TaskStatus } from '@/types/tasks.ts';
import { formatUUIDForLog } from '@/utils/logger/utils.ts';
import {
	BatchOptions,
	CONCURRENT_TASK_TYPES,
	ExecutionTask,
	TaskDefinition,
	TaskManagerStatus,
	TOTAL_TASK_STEPS,
	UITask,
} from '@/context/house/tasks/types.ts';
import { logger, logTask } from '@/context/house/tasks/logging.ts';
import { getHouseIdFromMeta, isTaskDelete, makeDeleteKeyFromMeta } from './helpers';
import { createRuntime } from './runtime';
import { actions as uiActions, uiTaskReducer } from './state/uiTaskReducer';
import { doesTaskBelongToHouse } from './managerUtils';
import { getUITaskStatus, MAX_CONCURRENT_DEFAULT } from '@/context/house/tasks/uiTask.ts';
import { processQueuedTasks as runTaskScheduling } from './TaskScheduler';
import { Batch, createBatchManager } from './batchManager';
import { createRetryPolicy } from './retryPolicy';
import { createCancelTask } from './actions/cancelTask';
import { createScheduleTaskBatch } from './actions/scheduleTaskBatch';
import { createRetryActions } from './actions/retryTask';
import {
	createGetActiveServerTasksByType,
	createGetConcurrencyLimits,
	createGetDispatchedPendingCountByType,
} from './concurrency';
import { useRecordStatusContext } from '@/context/house/documents/RecordStatusContext.tsx';
import { isDeleteComplete, isUploadComplete } from '@/utils/documentUtils.ts';

/**
 * Context interface for the House Task Manager
 */
interface HouseTaskManagerContextType {
	uiTasks: UITask[];
	getStatus: () => TaskManagerStatus;
	scheduleTaskBatch: (taskDefinitions: TaskDefinition[], options?: BatchOptions) => Promise<void>;
	cancelTask: (taskId: string) => Promise<void>;
	retryTask: (taskId: string) => Promise<void>;
	retryTasks: (taskIds: string[]) => Promise<void>;
	updateApiTaskMetadata: (taskId: string, metadataUpdates: Record<string, unknown>) => void;
}

/**
 * Context for sharing task manager state across components
 */
const HouseTaskManagerContext = createContext<HouseTaskManagerContextType | null>(null);

const getTaskMetadataValue = (task: UITask, key: string): unknown => {
	const executionMetadata = task.execution?.apiTask?.metadata as Record<string, unknown> | undefined;
	return executionMetadata?.[key] ?? task.metadata[key];
};

const getTaskMetadataNumber = (task: UITask, key: string): number | null => {
	const value = getTaskMetadataValue(task, key);
	if (typeof value === 'number') return value;
	if (typeof value === 'string') {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
};

const getTaskMetadataString = (task: UITask, key: string): string | null => {
	const value = getTaskMetadataValue(task, key);
	return typeof value === 'string' ? value : null;
};

/**
 * Provider props
 */
interface HouseTaskManagerProviderProps {
	house: House;
	children: ReactNode;
}

/**
 * Provider component that manages task state for a specific house
 * This replaces the individual hook instances with shared state
 */
export const HouseTaskManagerProvider: React.FC<HouseTaskManagerProviderProps> = ({ house, children }) => {
	// All the state and logic from the original useHouseTaskManager hook
	const [uiTasks, dispatch] = React.useReducer(uiTaskReducer, [] as UITask[]);
	useHouseTasks(house.id); // Only used for side effects (restore tasks)

	// Refs for managing batches and global task tracking
	const batchesRef = useRef<Map<string, Batch>>(new Map());
	const globalTasksRef = useRef<Map<string, UITask>>(new Map());
	const processingRef = useRef<boolean>(false);
	const uiTasksRef = useRef<UITask[]>([]);
	// Centralized runtime for ephemeral coordination state (dispatch/execution marks and delete keys)
	const runtimeRef = useRef(createRuntime());
	const runtime = runtimeRef.current;

	// Update the ref whenever uiTasks changes
	useEffect(() => {
		uiTasksRef.current = uiTasks;
	}, [uiTasks]);

	// Reconcile document tasks (upload/update/delete) with house-level document status (V3)
	const { getPolledStatus } = useRecordStatusContext();
	useEffect(() => {
		logger.debug('Reconcile effect running. uiTasks count:', uiTasks.length);

		// Find actionable items: not terminal, have a documentId, and have started execution
		const actionable = uiTasks.filter((it) => {
			const status = getUITaskStatus(it);
			const terminal =
				status === TaskStatus.Completed ||
				status === TaskStatus.Failed ||
				status === TaskStatus.Skipped ||
				status === TaskStatus.Cancelled;
			const documentId = getTaskMetadataValue(it, 'documentId');
			const progress = it.execution?.apiTask?.progress ?? 0;

			// Only check pipeline status for tasks that have started execution (progress > 0)
			// This prevents reconciliation from completing tasks based on stale pipeline data
			return !terminal && documentId && progress > 0;
		});

		logger.debug('Reconcile actionable items:', actionable.length, '/', uiTasks.length, actionable);

		actionable.forEach((it) => {
			const currentDocId = getTaskMetadataNumber(it, 'documentId');
			if (currentDocId === null) return;

			const previousDocId = getTaskMetadataNumber(it, 'previousDocumentId');

			const pipeline = getPolledStatus(currentDocId);

			if (!pipeline && previousDocId !== null) {
				logger.debug(
					'Reconcile document status missing for current document ID',
					currentDocId,
					'previous document ID:',
					previousDocId,
				);
			}

			logger.debug(
				'Reconcile task:',
				it.id,
				'currentDocId:',
				currentDocId,
				'previousDocId:',
				previousDocId,
				'pipeline:',
				pipeline,
				'bnd_v2:',
				pipeline?.bnd_v2,
			);

			// Check if document operation is complete (upload/update via uploaded_at, delete via is_deleted)
			const isComplete = isUploadComplete(pipeline) || isDeleteComplete(pipeline);

			if (isComplete) {
				logger.debug('Reconcile completing task:', it.id);
				// Mark the UI document operation as completed now
				dispatch(uiActions.updateStatus(it.id, 'completed', 100));

				// Invoke callbacks attached to this item (if present)
				if (it.execution?.onComplete) {
					it.execution.onComplete().catch(() => {});
				}

				// Call batch completion handler
				const batchId = getTaskMetadataString(it, 'batchId');
				if (batchId) {
					onTaskComplete(it.id, batchId).catch(() => {});
				}
			}
		});
	}, [uiTasks, getPolledStatus]);

	/** Updates the API task status within a UI task */
	const updateApiTaskStatus = useCallback((taskId: string, status: TaskStatus, updates?: { progress?: number }) => {
		dispatch(uiActions.updateStatus(taskId, status, updates?.progress));
	}, []);

	/** Updates the API task ID within a UI task (when upload is accepted) */
	const updateApiTaskId = useCallback((taskId: string, newApiTaskId: string) => {
		dispatch(uiActions.updateId(taskId, newApiTaskId));
	}, []);

	/** Updates the API task metadata within a UI task */
	const updateApiTaskMetadata = useCallback((taskId: string, metadataUpdates: Record<string, unknown>) => {
		dispatch(uiActions.updateMetadata(taskId, metadataUpdates));
	}, []);

	/** Updates the execution task within a UI task (for retries) */
	const updateExecutionTask = useCallback((taskId: string, executionUpdates: Partial<ExecutionTask>) => {
		dispatch(uiActions.updateExecution(taskId, executionUpdates));
	}, []);

	const dispatchers = useMemo(
		() => ({
			updateStatus: (id: string, status: TaskStatus, progress?: number) =>
				dispatch(uiActions.updateStatus(id, status, progress)),
		}),
		[dispatch],
	);

	// Concurrency helpers extracted
	// Note: Server-side task tracking removed - V3 architecture uses document status pipeline instead
	const getActiveServerTasksByType = useMemo(
		() =>
			createGetActiveServerTasksByType({
				uploadInProgressTasks: [],
				deleteInProgressTasks: [],
				updateInProgressTasks: [],
			}),
		[],
	);

	const getDispatchedPendingCountByType = useMemo(
		() =>
			createGetDispatchedPendingCountByType({
				runtime,
				globalTasksRef,
				uiTasksRef,
			}),
		[runtime, globalTasksRef, uiTasksRef],
	);

	const getConcurrencyLimits = useMemo(
		() =>
			createGetConcurrencyLimits({
				getActiveServerTasksByType,
				uiTasksRef,
				getDispatchedPendingCountByType,
			}),
		[getActiveServerTasksByType, uiTasksRef, getDispatchedPendingCountByType],
	);

	/** Executes an individual task with retry logic */
	const executeTask = useCallback(
		async (executionTask: ExecutionTask, signal: AbortSignal): Promise<void> => {
			const { executeFn, apiTask } = executionTask;
			const { id: uiTaskId } = apiTask;

			// Get batchId from API task metadata
			const batchId = apiTask.metadata.batchId as string;

			try {
				// If the task belongs to a different house than this provider, do not execute
				if (!doesTaskBelongToHouse(apiTask.metadata as Record<string, unknown>, house.id)) {
					logTask(
						uiTaskId,
						'started',
						'warn',
						`Skipping execution: task does not belong to current house ${house.id}`,
					);
					// Mark as failed (non-retriable) to complete batch accounting
					updateApiTaskStatus(uiTaskId, 'failed');
					updateApiTaskMetadata(uiTaskId, {
						error: 'House changed during execution',
						expectedHouseId: getHouseIdFromMeta(apiTask.metadata as Record<string, unknown>),
						currentHouseId: house.id,
					});
					await onTaskFailed(uiTaskId, batchId);
					return;
				}

				logTask(uiTaskId, 'started', 'info', `Starting execution in batch ${batchId}`);

				// Update status to in-progress immediately to prevent duplicate execution
				dispatch(uiActions.updateStatus(uiTaskId, 'in-progress', undefined));

				// Attempt the execution. Returns the document ID (V3)
				// This is the only place where we call the execution function
				// Transfer progress, throttled to whole percents: a 64MB upload would
				// otherwise dispatch on every chunk and rerender the whole queue.
				let lastReportedPercent = 0;
				const reportProgress = (percent: number) => {
					const wholePercent = Math.min(100, Math.max(0, Math.round(percent)));
					if (wholePercent <= lastReportedPercent) return;
					lastReportedPercent = wholePercent;
					dispatch(uiActions.updateStatus(uiTaskId, 'in-progress', wholePercent));
				};

				const newDocId = await executeFn(signal, reportProgress);
				// Acceptance is the cancellation boundary: after this point the server owns
				// the document pipeline and aborting the completed HTTP request has no effect.
				runtime.releaseAbortController(uiTaskId, signal);

				// null = the content is already in this Bundle (caught locally by the gate or
				// reported by the server) — nothing was created, so there is no pipeline to
				// await. Settle as skipped; onTaskSkipped aggregates the notification.
				if (newDocId === null) {
					const metadata = apiTask.metadata as Record<string, unknown>;
					const taskName = typeof metadata.filename === 'string' ? metadata.filename : String(apiTask.id);
					logTask(uiTaskId, TOTAL_TASK_STEPS, 'info', `Content already in this Bundle; skipping.`);
					dispatch(uiActions.updateStatus(uiTaskId, 'skipped', 100));
					await onTaskSkipped(uiTaskId, batchId, taskName);
					return;
				}

				logTask(
					uiTaskId,
					'documentAccepted',
					'info',
					`Document accepted (V3), id: '${formatUUIDForLog(String(newDocId))}'`,
				);

				// Persist document id in metadata for downstream consumers (overlay, list, guards)
				updateApiTaskMetadata(uiTaskId, { documentId: newDocId });

				// Update id but keep the item active (HTTP upload done, but awaiting pipeline creation).
				// The bar is full because the transfer is: every byte is on the server. What
				// remains is server-side processing, which the row's label carries instead.
				dispatch(uiActions.updateId(uiTaskId, String(newDocId)));
				dispatch(uiActions.updateStatus(uiTaskId, 'in-progress', 100));

				if (executionTask.onAccepted) {
					logTask(uiTaskId, 'documentAccepted', 'info', `Calling onAccepted callback.`);
					await executionTask.onAccepted();
				}
			} catch (error) {
				// cancelTask owns the UI transition and batch accounting. Do not turn
				// the expected Axios abort rejection into a failed or retried task.
				if (signal.aborted) {
					logTask(uiTaskId, TOTAL_TASK_STEPS, 'info', `Upload request cancelled.`);
					return;
				}

				logTask(
					uiTaskId,
					TOTAL_TASK_STEPS,
					'error',
					`Task failed: ${error instanceof Error ? error.message : String(error)}`,
				);

				if (shouldRetry(executionTask, error)) {
					scheduleRetry(executionTask, error);
				} else if (isQuotaExhausted(error)) {
					// Out of attempts against an allowance that refills over hours. Nothing failed and
					// no retry can help, so this settles as its own terminal state rather than borrowing
					// the failure one — a red row and a retry button would both be lies.
					logTask(
						uiTaskId,
						TOTAL_TASK_STEPS,
						'info',
						`Upload allowance spent after ${executionTask.retryCount} retries; pausing until it refills.`,
					);
					dispatch(uiActions.updateStatus(uiTaskId, 'paused', 0));
					dispatch(uiActions.updateMetadata(uiTaskId, { quotaExhausted: true }));
					await onTaskPaused(uiTaskId, batchId);
				} else {
					// No more retries or non-retriable error - mark as failed
					logTask(
						uiTaskId,
						TOTAL_TASK_STEPS,
						'warn',
						`Task failed permanently. Retries: ${executionTask.retryCount}, rate limited: ${isRateLimitError(error)}`,
					);

					// Mark as failed with retry count in metadata - update apiTask instead of UITask
					dispatch(uiActions.updateStatus(uiTaskId, 'failed', undefined));
					dispatch(
						uiActions.updateMetadata(uiTaskId, {
							error: error instanceof Error ? error.message : 'Unknown error',
							retryCount: executionTask.retryCount,
						}),
					);

					// Show notification for permanent failure
					if (executionTask.showNotification) {
						const metadata = apiTask.metadata as Record<string, unknown>;
						const maybeFileName = typeof metadata.filename === 'string' ? metadata.filename : null;
						const taskName = maybeFileName ?? apiTask.id;
						const message =
							executionTask.retryCount > 0
								? `Failed to upload ${taskName} after ${executionTask.retryCount + 1} attempts.`
								: `Failed to upload ${taskName}.`;

						executionTask.showNotification({
							description: message,
							type: 'error',
						});
					}

					await onTaskFailed(uiTaskId, batchId);
				}
			}
		},
		[updateApiTaskId, updateApiTaskStatus, updateApiTaskMetadata, house.id],
	);

	/**
	 * Processes queued tasks respecting concurrency limits
	 * Delegate to pure scheduler with injected dependencies
	 */
	const processQueuedTasks = useCallback(() => {
		// Call extracted scheduler with DI
		runTaskScheduling({
			getConcurrencyLimits,
			uiTasksRef,
			globalTasksRef,
			processingRef,
			runtime,
			houseId: house.id,
			executeTask,
			dispatchers,
		});
	}, [getConcurrencyLimits, uiTasksRef, globalTasksRef, processingRef, runtime, house.id, executeTask, dispatchers]);

	// Externalized managers
	const {
		onTaskComplete: onTaskCompleteImpl,
		onTaskFailed: onTaskFailedImpl,
		onTaskSkipped: onTaskSkippedImpl,
		onTaskCancelled: onTaskCancelledImpl,
		onTaskPaused: onTaskPausedImpl,
	} = useMemo(
		() =>
			createBatchManager({
				batchesRef,
				globalTasksRef,
				runtime,
				processQueuedTasks,
				house,
			}),
		[batchesRef, globalTasksRef, runtime, processQueuedTasks, house],
	);

	const { shouldRetry: shouldRetryImpl, scheduleRetry: scheduleRetryImpl } = useMemo(
		() =>
			createRetryPolicy({
				runtime,
				updateExecutionTask,
				processQueuedTasks,
				dispatch,
			}),
		[runtime, updateExecutionTask, processQueuedTasks, dispatch],
	);

	/**
	 * Determines if a task should be retried based on error and retry count
	 */
	const shouldRetry = useCallback(
		(executionTask: ExecutionTask, error: unknown): boolean => {
			return shouldRetryImpl(executionTask, error);
		},
		[shouldRetryImpl],
	);

	/**
	 * Schedules a retry for a failed task
	 * Uses non-blocking setTimeout for retry delay
	 */
	const scheduleRetry = useCallback(
		(executionTask: ExecutionTask, error?: unknown): void => {
			scheduleRetryImpl(executionTask, error);
		},
		[scheduleRetryImpl],
	);

	/**
	 * Handles task completion - checks if batch is complete
	 */
	const onTaskComplete = useCallback(
		async (taskId: string, batchId: string): Promise<void> => {
			return onTaskCompleteImpl(taskId, batchId);
		},
		[onTaskCompleteImpl],
	);

	/**
	 * Handles task failure - checks if batch is complete
	 */
	const onTaskFailed = useCallback(
		async (taskId: string, batchId: string): Promise<void> => {
			return onTaskFailedImpl(taskId, batchId);
		},
		[onTaskFailedImpl],
	);

	const onTaskSkipped = useCallback(
		async (taskId: string, batchId: string, duplicateFileName?: string): Promise<void> => {
			return onTaskSkippedImpl(taskId, batchId, duplicateFileName);
		},
		[onTaskSkippedImpl],
	);

	const onTaskCancelled = useCallback(
		async (taskId: string, batchId: string): Promise<void> => {
			return onTaskCancelledImpl(taskId, batchId);
		},
		[onTaskCancelledImpl],
	);

	const onTaskPaused = useCallback(
		async (taskId: string, batchId: string): Promise<void> => {
			return onTaskPausedImpl(taskId, batchId);
		},
		[onTaskPausedImpl],
	);

	/**
	 * Schedules a batch of tasks for execution
	 * All tasks appear in UI immediately, execution is managed by processQueuedTasks
	 */
	const scheduleTaskBatch = useMemo(
		() =>
			createScheduleTaskBatch({
				processQueuedTasks,
				uiTasksRef,
				globalTasksRef,
				batchesRef,
				runtime,
				dispatch,
				houseId: house.id,
			}),
		[processQueuedTasks, uiTasksRef, globalTasksRef, batchesRef, runtime, dispatch, house.id],
	);

	/**
	 * Gets current status of the task manager
	 */
	const getStatus = useCallback((): TaskManagerStatus => {
		const queuedTasks = uiTasks.filter((task) => isTaskQueued(task.execution.apiTask)).length;
		const activeTasks = uiTasks.filter((task) => isTaskInProgress(task.execution.apiTask)).length;
		const completedTasks = uiTasks.filter((task) => isTaskCompleted(task.execution.apiTask)).length;
		const failedTasks = uiTasks.filter((task) => isTaskFailed(task.execution.apiTask)).length;
		const skippedTasks = uiTasks.filter((task) => getUITaskStatus(task) === TaskStatus.Skipped).length;
		const cancelledTasks = uiTasks.filter((task) => getUITaskStatus(task) === TaskStatus.Cancelled).length;
		const totalLoad = activeTasks;

		return {
			totalTasks: uiTasks.length,
			queuedTasks,
			activeTasks,
			completedTasks,
			failedTasks,
			skippedTasks,
			cancelledTasks,
			totalLoad,
			canAcceptMore: totalLoad < MAX_CONCURRENT_DEFAULT, // Default max concurrent
		};
	}, [uiTasks]);

	// Auto-process queued tasks when client tasks change
	// V3: Server-side task tracking removed - only trigger on uiTasks changes
	// Add debouncing to prevent excessive retriggering
	useEffect(() => {
		const hasQueuedTasks = uiTasksRef.current.some((task) => isTaskQueued(task.execution.apiTask));

		// Check if any task type has available slots
		const uploadLimits = getConcurrencyLimits(CONCURRENT_TASK_TYPES.UPLOAD_RECORD);
		const deleteLimits = getConcurrencyLimits(CONCURRENT_TASK_TYPES.DELETE_RECORD);
		const updateLimits = getConcurrencyLimits(CONCURRENT_TASK_TYPES.UPDATE_RECORD);
		const hasAvailableSlots =
			uploadLimits.availableSlots > 0 || deleteLimits.availableSlots > 0 || updateLimits.availableSlots > 0;

		if (hasQueuedTasks && hasAvailableSlots) {
			// Use setTimeout with longer delay to debounce excessive calls
			const timeoutId = setTimeout(() => processQueuedTasks(), 100);
			return () => clearTimeout(timeoutId);
		}
	}, [uiTasks, getConcurrencyLimits, processQueuedTasks]); // Trigger when client-side tasks change

	// Extracted cancelTask logic for clarity and reuse
	const cancelTask = useMemo(
		() =>
			createCancelTask({
				globalTasksRef,
				uiTasksRef,
				runtime,
				dispatch,
				onTaskCancelled,
			}),
		[globalTasksRef, uiTasksRef, runtime, dispatch, onTaskCancelled],
	);

	/**
	 * Removes a task from the UI list and internal tracking, then schedules a new copy
	 * so that the queue shows a single entry while retrying.
	 */
	const { retryTask, retryTasks } = useMemo(
		() =>
			createRetryActions({
				globalTasksRef,
				uiTasksRef,
				runtime,
				dispatch,
				scheduleTaskBatch,
			}),
		[globalTasksRef, uiTasksRef, runtime, dispatch, scheduleTaskBatch],
	);

	// Context value
	const contextValue = useMemo(
		() => ({
			uiTasks,
			getStatus,
			scheduleTaskBatch,
			cancelTask,
			retryTask,
			retryTasks,
			updateApiTaskMetadata,
		}),
		[uiTasks, getStatus, scheduleTaskBatch, cancelTask, retryTask, retryTasks, updateApiTaskMetadata],
	);

	// Auto-resume: when KB update finishes, try to drain the queue immediately
	// useEffect(() => {
	// 	const hasQueuedTasks = uiTasksRef.current.some(task => isTaskQueued(task.execution.apiTask));
	// 	if (hasQueuedTasks) {
	// 		const id = setTimeout(() => processQueuedTasks(), 0);
	// 		return () => clearTimeout(id);
	// 	}
	// }, []);

	// When the provider's house changes, purge or keep only tasks for this house
	useEffect(() => {
		logger.info(`House changed to ${house.id}. Filtering tasks for this house. Current tasks: ${uiTasks.length}`);
		const before = uiTasksRef.current.length;
		const filtered = uiTasksRef.current.filter((t) => {
			const meta = t.execution.apiTask.metadata as Record<string, unknown>;
			const taskHouseId = getHouseIdFromMeta(meta);
			return taskHouseId === house.id;
		});
		const removed = before - filtered.length;
		logger.info(`House changed to ${house.id}. Kept ${filtered.length} tasks, removed ${removed}.`);
		// Rebuild tracking maps for the kept tasks
		globalTasksRef.current.clear();
		runtime.clear();
		filtered.forEach((t) => {
			globalTasksRef.current.set(t.id, t);
			if (isTaskDelete(t)) {
				const key = makeDeleteKeyFromMeta(t.execution.apiTask.metadata as Record<string, unknown>);
				if (key) runtime.activeDeleteKeys.add(key);
			}
		});
		batchesRef.current.clear();
		dispatch(uiActions.replaceAll(filtered));
	}, [house.id]);

	return <HouseTaskManagerContext.Provider value={contextValue}>{children}</HouseTaskManagerContext.Provider>;
};

/**
 * Hook to consume the House Task Manager context
 * Replaces the original useHouseTaskManager hook
 */
export const useHouseTaskManager = (): HouseTaskManagerContextType => {
	const context = useContext(HouseTaskManagerContext);
	if (!context) {
		throw new Error('useHouseTaskManager must be used within a HouseTaskManagerProvider');
	}
	return context;
};
